import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { I18nMap } from '../../../../../models/entities/i18n-map';
import { ToggleListComponent, ToggleListGroupDirective } from '../../../shared/toggle-list/toggle-list.component';
import { Membership } from '../../../../../models/entities/membership';
import { MembersService } from '../../../../../services/members/members.service';
import { GrowlerService } from '../../../../../services/growler.service';
import { AvatarBlockModule } from '../../../shared/avatar-block/avatar-block.module';
import { MatButtonModule } from '@angular/material/button';
import { ButtonSpacerDirective } from '../../../shared/directives/button.directive';
import { IsTypeofDirective } from '../../../shared/directives/typeof.directive';
import { I18nPluralPipe } from '@angular/common';
import { EmptyStateModule } from '../../../shared/empty-state/empty-state.module';
import { map } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatIconModule } from '@angular/material/icon';
import { MobileButtonPipe } from '../../../../../pipes/format/mobile-button.pipe';

export interface MemberAdminDialogInput {
    title: string;
    required: boolean;
    hideMissingEmail?: boolean;

    // preselected ids
    selectedMembershipIds?: number[];

    // scope membership to organization
    orgId?: number;
    // callout homeowner(s) via unitId
    unitId?: number;

    // provide confirmation button label with string or i18n-mapped object
    confirmButton?: string|I18nMap;

    // provide text to display when no memberships are found onload
    emptyStateLabel?: string;
}

export interface MemberAdminDialogOutput {
    success: true;
    selectedMemberships: Membership[];
}

@UntilDestroy()
@Component({
    selector: 'app-member-admin-dialog',
    templateUrl: './member-admin-dialog.component.html',
    standalone: true,
    imports: [
        MatDialogModule,
        ToggleListComponent,
        AvatarBlockModule,
        MatButtonModule,
        ButtonSpacerDirective,
        IsTypeofDirective,
        I18nPluralPipe,
        EmptyStateModule,
        MatIconModule,
        MobileButtonPipe,
        ToggleListGroupDirective,
    ],
})
export class MemberAdminDialogComponent implements OnInit
{
    @ViewChild(ToggleListComponent<Membership>) listRef: ToggleListComponent<Membership>;

    memberships: Membership[];
    groups: { [prop: string]: (membership: Membership) => boolean } = {};

    readonly confirmLabelMapping: I18nMap = {
        '=0': 'Select Members',
        '=1': 'Select 1 Member',
        other: 'Select # Members',
    };

    constructor(
            private _membersService: MembersService,
            private _matDialogRef: MatDialogRef<MemberAdminDialogComponent>,
            private _growler: GrowlerService,
            @Inject(MAT_DIALOG_DATA) public data: MemberAdminDialogInput,
    ) {
    }

    readonly adminMemberships = (membership: Membership) => !!membership.isAdmin;
    readonly ownerMemberships = (membership: Membership) => {
        if (!this.data.unitId) {
            return false;
        }

        return membership.owners.filter((owner) => owner.unitId === this.data.unitId).length > 0;
    };
    readonly disabledMemberships = (membership: Membership) => {
        return this.data.hideMissingEmail && !membership.user?.email;
    };

    ngOnInit() {
        const options: { [prop: string]: string|boolean } = {
            status: 'active',
            owners: true,
        };

        this._membersService.getMembers((this.data.orgId || null), options).pipe(
                map((res) => (res['memberships'] as any[]).map((membership: any) => new Membership(membership))),
                map((memberships) => {
                    return memberships.sort((a, b) => a.name.localeCompare(b.name));
                }),
                untilDestroyed(this),
        ).subscribe({
            next: (res) => {
                this.memberships = res.filter((membership) => !this.disabledMemberships(membership));
            },
            error: () => this._growler.oops('Unable to get requested members'),
        });
    }

    confirmSelection() {
        if (this.listRef.validate) {
            const output: MemberAdminDialogOutput = {
                success: true,
                selectedMemberships: this.listRef.combinedValue,
            };

            this._matDialogRef.close(output);
        }
    }
}
